import { Controller } from 'react-hook-form';
import Text from '../../atoms/Text';
import { useId, useState } from 'react';
import {
	CheckCircle,
	FileText,
	Trash,
	Upload,
	XCircle,
} from '@phosphor-icons/react';
import { Spinner } from '@/components/ui/spinner';

interface Props {
	name: string;
	control: any;
	accept?: string;
	rules?: object;
	errorMessage?: string;
	label?: string;
	sx?: string;
	onFileChange?: (file?: File) => void;
}

/**
 * File input controlled by react-hook-form: the form value IS the single
 * source of truth for the selected file. Deleting clears the form value (so
 * validation re-runs and a submit can never send a file the user removed),
 * and the native input resets after every pick so re-selecting the same file
 * fires change again.
 */
const InputFileNew = ({
	name,
	control,
	accept,
	rules,
	errorMessage,
	label,
	sx,
	onFileChange,
}: Props) => {
	const [isUploading, setIsUploading] = useState(false);
	// HTML id must be unique per instance — when two uploaders sharing the
	// same field name (e.g. e.firma + sellos on /tax-profile/) render at the
	// same time, the browser otherwise associates a label to the first
	// matching input id and the wrong form receives the file.
	const inputId = useId();

	return (
		<Controller
			control={control}
			rules={rules}
			name={name}
			render={({ field: { onChange, value } }) => {
				const file = value as File | undefined;

				const handleSelectFile = (
					event: React.ChangeEvent<HTMLInputElement>,
				) => {
					const selected = event.target.files?.[0];
					event.target.value = '';
					if (!selected) return;
					onChange(selected);
					onFileChange?.(selected);
					setIsUploading(true);
					setTimeout(() => {
						setIsUploading(false);
					}, 1500);
				};

				const handleDeleteFile = () => {
					onChange(undefined);
					onFileChange?.(undefined);
				};

				return file ? (
					<div>
						<div className='flex items-center border border-h-neutral-80 rounded-xl cursor-pointer px-4 pt-2 pb-1'>
							<div className='w-[95%]'>
								<label
									htmlFor={inputId}
									className={`flex flex-col w-full ${
										errorMessage ? 'border-h-red-40' : 'border-solid'
									}`}
								>
									<div className='flex items-center justify-between'>
										<div className='flex items-center'>
											<FileText
												size={24}
												weight='fill'
												color='var(--h-neutral-60)'
											/>
											<div className='flex flex-col ml-2'>
												<p>{label}</p>
												<p className='text-sm font-light max-w-[200px] md:max-w-full text-h-neutral-60 truncate'>
													{file.name}
												</p>
											</div>
										</div>

										<input
											id={inputId}
											className='hidden'
											onChange={handleSelectFile}
											type='file'
											accept={accept}
										/>
									</div>

									<div className='flex items-center justify-between gap-1 w-[90%]'>
										{isUploading ? (
											<div className='h-[6px] bg-h-primary-90/80 w-full rounded-lg'>
												<div className='h-[6px] bg-h-primary-50 w-[70%] rounded-lg' />
											</div>
										) : (
											<div
												className={`h-[6px] w-full rounded-lg ${errorMessage ? 'bg-h-red-40' : 'bg-h-success-50'}`}
											/>
										)}

										{isUploading ? (
											<Spinner
												className='size-4 ml-1'
											/>
										) : errorMessage ? (
											<XCircle
												size={16}
												weight='fill'
												color='var(--h-red-50)'
												className='mb-0.5'
											/>
										) : (
											<CheckCircle
												size={16}
												weight='fill'
												color='var(--h-success-50)'
												className='mb-0.5'
											/>
										)}
									</div>
								</label>
							</div>
							<div className='flex justify-center items-center'>
								<Trash
									size={20}
									color='var(--h-neutral-30)'
									onClick={handleDeleteFile}
								/>
							</div>
						</div>

						<div className='flex flex-row items-center gap-x-1 mt-1'>
							{!!errorMessage && (
								<XCircle
									size={18}
									weight='fill'
									color='var(--h-red-50)'
								/>
							)}
							<Text
								size='xs'
								sx='text-h-red-40'
							>
								{errorMessage}
							</Text>
						</div>
					</div>
				) : (
					<div>
						<label
							htmlFor={inputId}
							className={`cursor-pointer border-h-neutral-80 p-2 border rounded-lg flex flex-row items-center gap-2 justify-between border-solid`}
						>
							<div
								className={`flex flex-row items-center gap-x-2 truncate pr-2 justify-between w-full ${sx}`}
							>
								<div className='bg-h-primary-90/80 text-h-primary-40 px-4 py-2 rounded-full'>
									<p>{label}</p>
								</div>

								<Upload
									size={22}
									weight='light'
									color=''
								/>

								<input
									id={inputId}
									className='hidden'
									onChange={handleSelectFile}
									type='file'
									accept={accept}
								/>
							</div>
						</label>
						{!!errorMessage && (
							<div className='flex flex-row items-center gap-x-1 mt-1'>
								<XCircle
									size={18}
									weight='fill'
									color='var(--h-red-50)'
								/>
								<Text
									size='xs'
									sx='text-h-red-40'
								>
									{errorMessage}
								</Text>
							</div>
						)}
					</div>
				);
			}}
		/>
	);
};

export default InputFileNew;
